import { useEffect, useState } from "react";
import { apiFetch } from "../../lib/api.js";
import { useToast } from "../Toast.js";
import {
  FOUNDRY_ENDPOINT_ERROR,
  FOUNDRY_ENDPOINT_PLACEHOLDER,
  aiProviderLabel,
  isValidFoundryEndpoint,
  providerServesFamily,
  webSearchHelp,
  type AiProviderConfigShape,
  type ModelCandidates
} from "../../lib/aiProvider.js";

/**
 * ADR-0207 §3.2 — the AI provider connection's detail body. Fields are always
 * editable and save themselves: text fields when you leave them, the web
 * search box when you tick it (UI_GUIDELINES §1.6). Silent on success, a toast
 * on failure. The key field is always empty; entering a key replaces the
 * stored one. The provider itself never changes — remove and re-add instead.
 */

interface ConsentPackage {
  decision_pack_url: string;
  sendable_text: string;
}

export function AiProviderIntegrationBody(props: {
  integrationId: string;
  config: AiProviderConfigShape;
  credsAuditLine: string | null;
  onReload: () => void;
  onTest: () => void;
}): JSX.Element {
  const toast = useToast();
  const provider = props.config.provider_kind;

  // What the server holds, and the local edits; a field saves only when it differs.
  const [saved, setSaved] = useState<AiProviderConfigShape>(props.config);
  const [endpoint, setEndpoint] = useState(props.config.endpoint ?? "");
  const [endpointError, setEndpointError] = useState<string | null>(null);
  const [deployments, setDeployments] = useState<Record<string, string>>(props.config.deployments ?? {});
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [candidates, setCandidates] = useState<ModelCandidates | null>(null);
  const [pkg, setPkg] = useState<ConsentPackage | null>(null);
  const [pkgBusy, setPkgBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setCandidates(await apiFetch<ModelCandidates>("/api/tenant/admin/ai-provider/model-candidates"));
      } catch (e) {
        toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function patch(body: { config?: Partial<AiProviderConfigShape>; credentials?: { api_key: string } }): Promise<boolean> {
    try {
      const res = await apiFetch<{ integration: { config: AiProviderConfigShape } }>(
        `/api/tenant/admin/integrations/${props.integrationId}`,
        { method: "PATCH", body }
      );
      if (res.integration?.config) setSaved(res.integration.config);
      return true;
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      return false;
    }
  }

  function saveEndpoint() {
    const v = endpoint.trim();
    if (!isValidFoundryEndpoint(v)) {
      setEndpointError(FOUNDRY_ENDPOINT_ERROR);
      return;
    }
    setEndpointError(null);
    if (v === (saved.endpoint ?? "")) return;
    void patch({ config: { endpoint: v } });
  }

  function saveDeployments() {
    const same = JSON.stringify(normalizeDeployments(deployments)) === JSON.stringify(normalizeDeployments(saved.deployments ?? {}));
    if (same) return;
    void patch({ config: { deployments } });
  }

  function saveKey() {
    const k = apiKey.trim();
    if (k === "") return;
    void (async () => {
      if (await patch({ credentials: { api_key: k } })) {
        setApiKey("");
        props.onReload(); // the audit line picks up the new credentials time
      }
    })();
  }

  async function getPackage() {
    setPkgBusy(true);
    try {
      setPkg(await apiFetch<ConsentPackage>(`/api/tenant/admin/integrations/${props.integrationId}/consent-package`));
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setPkgBusy(false);
    }
  }

  async function copySendable() {
    if (!pkg) return;
    try {
      await navigator.clipboard.writeText(pkg.sendable_text);
      toast.push({ kind: "success", message: "Copied — paste it into an email to the person who approves your AI account." });
    } catch {
      toast.push({ kind: "error", message: "Could not copy — select the text and copy manually." });
    }
  }

  const showWebSearch = providerServesFamily(provider, candidates?.web_search_model_family);
  const webHelp = webSearchHelp(provider);

  return (
    <>
      <div className="card" style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <span className="pill">AI provider</span> <strong>{aiProviderLabel(provider)}</strong>
          </div>
          <button type="button" className="btn" onClick={props.onTest}>
            Test connection
          </button>
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          <label className="field">
            <span className="label">API key</span>
            <div style={{ display: "flex", gap: 6 }}>
              <input
                className="input"
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                onBlur={saveKey}
                placeholder="Enter a new key to replace the current one"
                autoComplete="off"
                style={{ flex: 1 }}
              />
              <button type="button" className="btn" onClick={() => setShowKey((v) => !v)} aria-label={showKey ? "Hide key" : "Show key"}>
                {showKey ? "Hide" : "Show"}
              </button>
            </div>
            <div className="help">
              Stored encrypted. Atlas never shows it again.
              {props.credsAuditLine ? (
                <>
                  <br />
                  <span className="muted">{props.credsAuditLine}</span>
                </>
              ) : null}
            </div>
          </label>

          {provider === "azure_foundry" ? (
            <>
              <label className="field">
                <span className="label">Resource endpoint</span>
                <input
                  className="input"
                  type="text"
                  value={endpoint}
                  onChange={(e) => setEndpoint(e.target.value)}
                  onBlur={saveEndpoint}
                  placeholder={FOUNDRY_ENDPOINT_PLACEHOLDER}
                />
                {endpointError ? <div className="help" style={{ color: "var(--danger-text)" }}>{endpointError}</div> : null}
              </label>

              <div className="field">
                <span className="label">Deployments</span>
                <div className="help">
                  Enter the deployment name for each model you&rsquo;ve deployed in Foundry. Leave a row blank if that model
                  isn&rsquo;t deployed — Atlas won&rsquo;t offer it.
                </div>
                {!candidates ? (
                  <div className="muted">Loading…</div>
                ) : (
                  <div className="tableWrap">
                    <table className="table tableCompact">
                      <thead>
                        <tr>
                          <th>Model</th>
                          <th>Deployment name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {candidates.candidates.map((c) => (
                          <tr key={c.model_id}>
                            <td className="mono">{c.model_id}</td>
                            <td>
                              <input
                                className="input"
                                type="text"
                                value={deployments[c.model_id] ?? ""}
                                placeholder={c.model_id}
                                onChange={(e) => setDeployments({ ...deployments, [c.model_id]: e.target.value })}
                                onBlur={saveDeployments}
                                aria-label={`Deployment name for ${c.model_id}`}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : null}

          {showWebSearch ? (
            <label className="field">
              <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={saved.web_search_enabled === true}
                  onChange={(e) => void patch({ config: { web_search_enabled: e.target.checked } })}
                />
                <span className="label" style={{ margin: 0 }}>Allow web search</span>
              </span>
              {webHelp ? <div className="help">{webHelp}</div> : null}
            </label>
          ) : null}
        </div>
      </div>

      <div className="card" style={{ display: "grid", gap: 12 }}>
        <div style={{ fontWeight: 700 }}>Decision package</div>
        <div className="muted" style={{ fontSize: 13 }}>
          For the person who approves your organization&rsquo;s AI account: what Atlas sends, where it goes, who
          processes it, and how to revoke it.
        </div>
        <div className="actions">
          <button type="button" className="btn" onClick={() => void getPackage()} disabled={pkgBusy}>
            {pkgBusy ? "Preparing…" : "Get decision package"}
          </button>
        </div>
        {pkg ? (
          <div style={{ display: "grid", gap: 8 }}>
            <textarea className="input" readOnly rows={7} value={pkg.sendable_text} style={{ fontSize: 12 }} />
            <div className="actions">
              <button type="button" className="btn btnPrimary" onClick={() => void copySendable()}>
                Copy to clipboard
              </button>
              <a className="btn" href={pkg.decision_pack_url} target="_blank" rel="noreferrer">
                Preview the decision document
              </a>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

function normalizeDeployments(d: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of Object.keys(d).sort()) {
    const v = (d[k] ?? "").trim();
    if (v) out[k] = v;
  }
  return out;
}
